/**
 * DropboxCallbackPage
 *
 * Minimal popup bridge page. Dropbox redirects the OAuth popup here after
 * authorization. This page reads the code/state from the URL, sends them to
 * the opener window via postMessage, then closes itself.
 *
 * Route: /dropbox/callback (public, no auth required)
 */
import { useEffect, useState } from 'react';

export function DropboxCallbackPage() {
  const [status, setStatus] = useState<'processing' | 'done' | 'error'>('processing');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');

    if (window.opener) {
      window.opener.postMessage(
        { type: 'DROPBOX_OAUTH_CALLBACK', code, state, error },
        window.location.origin
      );
      setStatus('done');
      // Small delay so the user sees "connected" message before close
      setTimeout(() => window.close(), 500);
    } else {
      // Opened directly (not as popup) — redirect to settings
      setStatus('error');
      setTimeout(() => {
        window.location.href = '/settings?tab=integrations';
      }, 2000);
    }
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center space-y-2">
        {status === 'processing' && (
          <>
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm text-gray-600">Connecting Dropbox...</p>
          </>
        )}
        {status === 'done' && (
          <p className="text-sm text-green-700 font-medium">Connected! Closing...</p>
        )}
        {status === 'error' && (
          <p className="text-sm text-gray-600">Redirecting to settings...</p>
        )}
      </div>
    </div>
  );
}
